/**
 * AccessGate — the bouncer. Answers one question: is this person allowed in?
 *
 * The door is built now and left open: m3cull is free while we find out whether
 * anyone wants it. When that changes, only this class and its two collaborators
 * need to learn about licences — no feature service ever checks entitlement, so
 * there is nothing to retrofit.
 */

import type { AccessDecision } from '../types';

/** Where a licence key is kept. Trivial today, a real store later. */
export interface LicenceStore {
  read(): string | null;
  write(key: string): void;
  clear(): void;
}

/** Whoever can say if a key is real. Today nobody asks. */
export interface LicenceVerifier {
  verify(key: string): Promise<boolean>;
}

export class AccessGate {
  constructor(
    private readonly mode: 'free' | 'licensed' = 'free',
    private readonly store?: LicenceStore,
    private readonly verifier?: LicenceVerifier,
  ) {}

  async decide(): Promise<AccessDecision> {
    if (this.mode === 'free') {
      return { allowed: true, reason: 'free' };
    }

    const key = this.store?.read() ?? null;
    if (!key) return { allowed: false, reason: 'no-licence' };

    const valid = (await this.verifier?.verify(key)) ?? false;
    return valid
      ? { allowed: true, reason: 'valid-licence' }
      : { allowed: false, reason: 'invalid-licence' };
  }
}
